import { MarkdownSerializer, defaultMarkdownSerializer } from 'prosemirror-markdown';

/**
 * Serializes a custom node.
 *
 * @param name - The name of the custom node.
 * @param type - The type of the custom node. Defaults to 'inline'.
 * @param filter - A function to filter the attributes of the custom node.
 * @returns A function that serializes the custom node.
 */
const serializeCustomNode =
  ({
    name,
    type = 'inline',
    filter = () => true,
  }: {
    name: string;
    type?: 'inline' | 'block';
    filter?: (key: string, value: any) => boolean;
  }): MarkdownSerializer['nodes'][number] =>
  (state, node) => {
    const attrs = Object.entries(node.attrs)
      .filter(([key, value]) => {
        if (value == null) return false;
        if (typeof value === 'undefined') return false;

        return filter(key, value);
      })
      .map(([key, value]) => {
        const safeValue = String(value)
          .replace(/\./g, '\\u002E')
          .replace(/,/g, '\\u002C')
          .replace(/\{/g, '\\u007B')
          .replace(/\}/g, '\\u007D')
          .replace(/:/g, '\\u003A');

        return `${key}:${safeValue}`;
      })
      .join(',');

    state.write(`@${name}:{${attrs}}`);

    if (type === 'block') state.write('\n\n');
  };

export const getSerializer = () =>
  new MarkdownSerializer(
    {
      ...defaultMarkdownSerializer.nodes,
      bulletList: defaultMarkdownSerializer.nodes.bullet_list,
      orderedList: defaultMarkdownSerializer.nodes.ordered_list,
      listItem: defaultMarkdownSerializer.nodes.list_item,
      hardBreak: defaultMarkdownSerializer.nodes.hard_break,
      taskList: (state, node) => {
        state.renderList(node, '  ', () => '- ');
      },
      taskItem: (state, node) => {
        state.write(`[${node.attrs.checked ? 'x' : ' '}] `);
        state.renderContent(node);
      },
      horizontalRule: defaultMarkdownSerializer.nodes.horizontal_rule,
      codeBlock: defaultMarkdownSerializer.nodes.code_block,
      mention: serializeCustomNode({ name: 'mention' }),
      document: serializeCustomNode({
        name: 'document',
        type: 'block',
        filter: (key) => {
          return !['uploading', 'show_preview'].includes(key);
        },
      }),
    },
    {
      ...defaultMarkdownSerializer.marks,
      bold: defaultMarkdownSerializer.marks.strong,
      italic: defaultMarkdownSerializer.marks.em,
      underline: {
        open: '__',
        close: '__',
        mixable: true,
        expelEnclosingWhitespace: true,
      },
      strike: {
        open: '~~',
        close: '~~',
        mixable: true,
        expelEnclosingWhitespace: true,
      },
    }
  );
